/*
 * Dropdown for the vehicle picker drawer, as in the competitor's "Edit Vehicle" box: the field button opens a panel
 * with a "Search..." box over the option list and focus moves into that box. The box is the WAI-ARIA combobox
 * (aria-activedescendant follows the highlighted row); rows filter and rank with the rules shared with the homepage
 * bar (seatfit-picker-search.js). Phones use the native <select> next to it, unless the "Search box on phones"
 * setting loads seatfit-vehicle-picker-mobile-sheet.js (full-screen list). Loaded `defer` by the section after
 * seatfit-picker-search.js and before seatfit-vehicle-picker.js (which stays off without this combobox).
 */
(function () {
  'use strict';
  if (window.SeatFitPickerCombo || !window.SeatFitPickerSearch) return;
  var S = window.SeatFitPickerSearch;

  var MAX_POP_PX = 320; // search box + list
  var current = null; // only one list open at a time across all fields

  function rowOf(target) { return target && target.closest ? target.closest('[data-i]') : null; }

  function Combo(field, onPick) {
    this.field = field;
    this.btn = field.querySelector('.sf-vp__combo');
    this.val = field.querySelector('.sf-vp__val');
    this.pop = field.querySelector('.sf-vp__pop');
    this.q = field.querySelector('.sf-vp__q');
    this.list = field.querySelector('.sf-vp__list');
    this.onPick = onPick;
    this.options = [];
    this.value = this.sig = null;
    this.matched = false;
    this.onDocDown = this.onDocDown.bind(this);
    var self = this;
    this.btn.addEventListener('click', function () { if (self.isOpen()) self.close(); else self.open(); });
    this.btn.addEventListener('keydown', function (e) { self.onButtonKey(e); });
    this.q.addEventListener('input', function () { self.applyFilter(); });
    this.q.addEventListener('keydown', function (e) { self.onKey(e); });
    this.list.addEventListener('mousedown', function (e) { e.preventDefault(); }); // keeps focus in the search box
    this.list.addEventListener('click', function (e) {
      var li = rowOf(e.target);
      if (li) self.pick(Number(li.getAttribute('data-i')));
    });
    this.list.addEventListener('mousemove', function (e) {
      var li = rowOf(e.target);
      if (li && li !== self.activeRow()) self.setActive(li, true);
    });
  }

  Combo.prototype.isOpen = function () { return !this.pop.hidden; };
  Combo.prototype.items = function () { return this.list.querySelectorAll('[data-i]'); };
  Combo.prototype.activeRow = function () { return this.list.querySelector('.is-active'); };
  // Rebuilding a 100-year list on every render would lose the scroll position, so only when it changed.
  Combo.prototype.setOptions = function (options, value, emptyLabel) {
    var sig = options.map(function (o) { return o.value; }).join('|');
    if (sig !== this.sig) {
      this.sig = sig;
      var frag = document.createDocumentFragment();
      var base = this.list.id;
      options.forEach(function (o, i) {
        var li = document.createElement('li');
        li.id = base + '-' + i;
        li.className = 'sf-vp__opt';
        li.setAttribute('role', 'option');
        li.setAttribute('data-i', String(i));
        li.textContent = o.label;
        frag.appendChild(li);
      });
      if (!options.length) {
        var empty = document.createElement('li');
        empty.className = 'sf-vp__empty';
        empty.setAttribute('role', 'option');
        empty.setAttribute('aria-disabled', 'true');
        empty.textContent = emptyLabel || '';
        frag.appendChild(empty);
      }
      this.list.textContent = '';
      this.list.appendChild(frag);
    }
    this.options = options;
    this.value = value == null ? null : String(value);
    var self = this, label = '';
    options.forEach(function (o) { if (o.value === self.value) label = o.label; });
    // A search may have moved rows into ranked order, so each row finds its option by data-i.
    Array.prototype.forEach.call(this.items(), function (li) {
      var o = options[Number(li.getAttribute('data-i'))];
      li.setAttribute('aria-selected', o && o.value === self.value ? 'true' : 'false');
    });
    if (this.val) this.val.textContent = label;
    this.field.toggleAttribute('data-filled', !!label);
    // A list that arrives while the panel is open (vehicle data still loading) takes the typed text too.
    if (this.isOpen()) this.applyFilter();
  };

  // text: a letter typed on the closed field goes into the search box instead of being lost.
  Combo.prototype.open = function (text) {
    if (this.btn.disabled || this.isOpen()) return;
    if (current && current !== this) current.close();
    current = this;
    this.pop.hidden = false;
    this.btn.setAttribute('aria-expanded', 'true');
    this.q.setAttribute('aria-expanded', 'true');
    this.q.value = text || '';
    this.place();
    this.applyFilter();
    this.q.focus({ preventScroll: true });
    document.addEventListener('pointerdown', this.onDocDown, true);
  };
  // Closing clears the typed text: the next open shows the whole list (unlike the competitor, which keeps it).
  Combo.prototype.close = function () {
    if (current === this) current = null;
    if (!this.isOpen()) return;
    var hadFocus = document.activeElement === this.q;
    this.pop.hidden = true;
    this.q.value = '';
    S.filter(this.items(), '');
    this.setActive(null);
    this.btn.setAttribute('aria-expanded', 'false');
    this.q.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.onDocDown, true);
    if (hadFocus) this.btn.focus({ preventScroll: true }); // Esc / Tab / pick carry on from the field
  };

  // Open downwards like the competitor; flip up when the field sits too low (Model on short screens).
  Combo.prototype.place = function () {
    var r = this.btn.getBoundingClientRect();
    var below = window.innerHeight - r.bottom - 12;
    var above = r.top - 12;
    var up = below < 200 && above > below;
    this.field.classList.toggle('is-up', up);
    this.pop.style.maxHeight = Math.max(160, Math.min(MAX_POP_PX, up ? above : below)) + 'px';
  };

  // Typing highlights no row (as on the competitor); an empty box shows the chosen row, scrolled into view. Nothing
  // found shows one grey "No match" row. A Model list passes the chosen make: "ram 1500" finds RAM's 1500.
  Combo.prototype.applyFilter = function () {
    var q = this.q.value.trim();
    var make = this.field.getAttribute('data-key') === 'model' &&
      this.field.parentNode.querySelector('[data-key="make"] .sf-vp__val');
    this.matched = S.filter(this.items(), q, 'sf-vp__empty', make ? make.textContent.trim() : '').matched;
    this.list.scrollTop = 0;
    this.setActive(q ? null : this.list.querySelector('[aria-selected="true"]'));
  };

  // byPointer: hovering must not scroll the list under the mouse.
  Combo.prototype.setActive = function (li, byPointer) {
    var old = this.activeRow();
    if (old && old !== li) old.classList.remove('is-active');
    if (!li) { this.q.removeAttribute('aria-activedescendant'); return; }
    li.classList.add('is-active');
    this.q.setAttribute('aria-activedescendant', li.id);
    if (!byPointer) S.reveal(this.list, li);
  };

  Combo.prototype.pick = function (i) {
    var o = this.options[i];
    if (!o) return;
    this.close();
    this.onPick(o.value);
  };
  // Field button while the list is closed (once open, focus sits in the search box).
  Combo.prototype.onButtonKey = function (e) {
    var k = e.key;
    if (e.ctrlKey || e.metaKey || e.altKey || this.isOpen()) return;
    if (k === 'ArrowDown' || k === 'ArrowUp' || k === 'Enter' || k === ' ') {
      e.preventDefault(); // otherwise the button's own click would toggle the list a second time
      this.open();
    } else if (k.length === 1) {
      e.preventDefault();
      this.open(k);
    }
  };

  // Keys typed in the search box.
  Combo.prototype.onKey = function (e) {
    var k = e.key;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    var vis = S.visible(this.items());
    var cur = this.activeRow();
    var next = S.step(vis, cur, k);
    if (next !== undefined) {
      e.preventDefault();
      if (next) this.setActive(next);
    } else if (k === 'Enter') {
      e.preventDefault();
      var li = S.enterRow(vis, cur, this.matched);
      if (li) this.pick(Number(li.getAttribute('data-i')));
    } else if (k === 'Escape') {
      e.preventDefault();
      e.stopPropagation(); // Esc closes the list first; only a second Esc closes the drawer
      this.close();
    } else if (k === 'Tab') {
      this.close(); // focus is back on the field button; the browser's Tab then moves on from there
    }
  };

  Combo.prototype.onDocDown = function (e) {
    if (!this.field.contains(e.target)) this.close();
  };

  window.SeatFitPickerCombo = Combo;
})();
